<template>
  <section class="manual-diagnostics border rounded-3 p-3 p-md-4 mb-4" aria-labelledby="manual-diagnostics-title">
    <div class="d-flex flex-column flex-md-row justify-content-between gap-2 mb-3">
      <div>
        <h2 id="manual-diagnostics-title" class="h4 mb-1">Check the sensors and controls</h2>
        <p class="mb-0 text-muted">
          Follow one short instruction at a time. The page only listens to Biotron; it does not change any setting.
        </p>
      </div>
      <button v-if="standaloneDownload" type="button" class="btn btn-outline-primary align-self-md-start"
              :disabled="!session" @click="downloadReport">Download this section</button>
    </div>

    <div class="alert py-2" :class="headlineClass" role="status" aria-live="polite">{{ headline }}</div>

    <p v-if="!capturing && !captureFinished" class="mb-3">
      Have the plant clips and light sensor within reach. You will get three seconds to prepare before the first action.
    </p>

    <button v-if="!captureFinished" type="button" class="btn btn-primary mb-3" :disabled="!canStart" @click="startCapture">
      {{ capturing ? "Sensor check is running…" : "Check the plant and light sensors · about 25 seconds" }}
    </button>

    <div v-if="capturing" class="manual-prompt rounded-3 p-3 mb-3">
      <strong aria-live="polite">{{ currentPhase.prompt }}</strong>
      <span class="badge text-bg-dark ms-2" aria-hidden="true">{{ secondsLeft }}s</span>
    </div>

    <section v-if="captureFinished && currentAttestation" class="manual-observation border rounded-3 p-3 mb-3"
             :aria-labelledby="`manual-observation-${currentAttestation.id}`">
      <p class="small text-uppercase text-muted mb-1">Observation {{ attestationIndex + 1 }} of {{ humanChecks.length }}</p>
      <h3 :id="`manual-observation-${currentAttestation.id}`" class="h5">{{ currentAttestation.label }}</h3>
      <p><strong>Do this:</strong> {{ instructionFor(currentAttestation.id).action }}</p>
      <p><strong>Expected:</strong> {{ instructionFor(currentAttestation.id).expected }}</p>
      <div class="d-flex flex-wrap gap-2">
        <button type="button" class="btn btn-outline-success"
                @click="attest(currentAttestation.id, 'pass')">Observed — {{ currentAttestation.label }}</button>
        <button type="button" class="btn btn-outline-danger"
                @click="attest(currentAttestation.id, 'fail')">Not observed — {{ currentAttestation.label }}</button>
        <button type="button" class="btn btn-outline-dark"
                @click="attest(currentAttestation.id, 'blocked')">Could not check — {{ currentAttestation.label }}</button>
      </div>
    </section>

    <p v-else-if="captureFinished" class="alert alert-success" role="status">
      Physical observations recorded. Review the section results below.
    </p>

    <details v-if="session" class="manual-details mt-3" :open="summary !== 'pass'">
      <summary class="fw-semibold">See what was checked</summary>
      <ol class="list-group list-group-numbered mt-2">
      <li v-for="check in checks" :key="check.id"
          class="list-group-item d-flex flex-column flex-lg-row justify-content-between gap-2">
        <div class="ms-2 me-auto">
          <span>{{ check.label }}</span>
          <small class="d-block text-muted">{{ kindLabel(check.kind) }}</small>
        </div>
        <div class="d-flex flex-wrap align-items-center gap-2 ms-2">
          <span class="badge rounded-pill" :class="statusClass(checkStatus(check.id))">
            {{ statusLabel(checkStatus(check.id)) }}
          </span>
        </div>
      </li>
      </ol>
    </details>

    <p class="small text-muted mt-3 mb-0">
      The report keeps your answers separate from checks made automatically by the browser.
    </p>
  </section>
</template>

<script>
import {
  BIOTRON_MANUAL_CHECKS,
  BIOTRON_MANUAL_PHASES,
  appendBiotronManualEvent,
  appendBiotronManualEvidence,
  biotronManualSummary,
  createBiotronManualReport,
  createBiotronManualSession,
  evaluateBiotronManualCapture,
  invalidateBiotronManualSession
} from "@/assets/js/BiotronManualDiagnostics"
import {sha256Hex} from "@/assets/js/BiotronSafeInspector"

const randomSessionId = () => {
  const values = new Uint32Array(2)
  crypto.getRandomValues(values)
  return Array.from(values, value => value.toString(16).padStart(8, "0")).join("")
}

export default {
  name: "BiotronManualDiagnostics",
  props: {
    deviceConnected: {type: Boolean, default: false},
    firmwareVersion: {type: String, default: ""},
    inspectorSummary: {type: String, default: "incomplete"},
    deviceEpoch: {type: Number, default: 0},
    midiEvent: {type: Object, default: null},
    plantChannel: {type: Number, default: 1},
    lightChannel: {type: Number, default: 2},
    lightMuted: {type: Boolean, default: false},
    mutationEpoch: {type: Number, default: 0},
    standaloneDownload: {type: Boolean, default: true}
  },
  emits: ["capture_state_changed", "manual_summary_changed", "manual_report_changed"],
  data() {
    return {
      checks: BIOTRON_MANUAL_CHECKS,
      phases: BIOTRON_MANUAL_PHASES,
      session: null,
      phaseIndex: -1,
      secondsLeft: 0,
      timer: null,
      captureFinished: false,
      attestationIndex: 0
    }
  },
  computed: {
    capturing() { return this.phaseIndex >= 0 && !this.captureFinished },
    currentPhase() { return this.phases[this.phaseIndex] || {id: "", prompt: ""} },
    humanChecks() { return this.checks.filter(check => check.kind === "human_attestation") },
    currentAttestation() { return this.humanChecks[this.attestationIndex] || null },
    canStart() {
      return !this.capturing && this.deviceConnected && this.firmwareVersion === "1.9.1" &&
        this.inspectorSummary === "pass"
    },
    summary() { return biotronManualSummary(this.session) },
    headline() {
      if (this.session && !this.session.valid) return "This result expired because Biotron or the connection changed. Run the connection check again."
      if (this.inspectorSummary !== "pass") return "Complete the connection check above before testing the sensors."
      if (this.capturing) return "Do only the action shown below. The page is listening to Biotron and changing nothing."
      if (this.summary === "pass") return "Sensor and control check complete. Your answers are saved in the support report."
      if (this.summary === "fail") return "A problem was observed. Stop here and download the support report."
      if (this.captureFinished) return "The sensor check is finished. Answer the four short questions below."
      return "Ready to check the plant clips and light sensor."
    },
    headlineClass() {
      if (this.summary === "pass") return "alert-success"
      if (this.summary === "fail") return "alert-danger"
      return "alert-secondary"
    }
  },
  watch: {
    midiEvent(event) {
      if (this.capturing && this.session && event) {
        this.session = appendBiotronManualEvent(this.session, this.currentPhase.id, event)
      }
    },
    deviceConnected(connected) { if (!connected) this.invalidate("device_disconnected") },
    firmwareVersion(value, previous) { if (this.session && value !== previous) this.invalidate("firmware_changed") },
    inspectorSummary(value) { if (this.session && value !== "pass") this.invalidate("inspector_invalidated") },
    mutationEpoch(value, previous) {
      if (this.session && value !== previous) this.invalidate("device_state_changed")
    },
    deviceEpoch(value, previous) {
      if (this.session && value !== previous && value !== this.session.device_epoch) this.invalidate("device_session_changed")
    },
    summary(value) { this.$emit("manual_summary_changed", value) },
    session() { this.emitReport() }
  },
  beforeUnmount() {
    this.clearTimer()
  },
  methods: {
    startCapture() {
      if (!this.canStart) return
      this.clearTimer()
      this.session = createBiotronManualSession({
        sessionId: randomSessionId(), deviceEpoch: this.deviceEpoch,
        plantChannel: this.plantChannel, lightChannel: this.lightChannel,
        lightMuted: this.lightMuted
      })
      this.captureFinished = false
      this.attestationIndex = 0
      this.$emit("capture_state_changed", true)
      this.startPhase(0)
    },
    startPhase(index) {
      this.clearTimer()
      if (index >= this.phases.length) {
        this.phaseIndex = -1
        this.captureFinished = true
        this.session = evaluateBiotronManualCapture(this.session)
        this.$emit("capture_state_changed", false)
        return
      }
      this.phaseIndex = index
      this.secondsLeft = this.phases[index].seconds
      this.timer = setInterval(() => {
        this.secondsLeft--
        if (this.secondsLeft <= 0) this.startPhase(index + 1)
      }, 1000)
    },
    clearTimer() {
      if (this.timer) clearInterval(this.timer)
      this.timer = null
    },
    invalidate(reason) {
      this.clearTimer()
      this.phaseIndex = -1
      this.captureFinished = false
      this.attestationIndex = 0
      this.$emit("capture_state_changed", false)
      if (this.session) this.session = invalidateBiotronManualSession(this.session, reason)
    },
    attest(check, status) {
      if (!this.session || !this.captureFinished) return
      this.session = appendBiotronManualEvidence(this.session, {
        check, status, kind: "human_attestation",
        reason: status === "pass" ? "operator_confirmed_yes" :
          status === "fail" ? "operator_confirmed_no" : "operator_not_sure"
      })
      this.attestationIndex++
    },
    checkStatus(id) {
      if (!this.session) return "pending"
      if (!this.session.valid) return "stale"
      const evidence = this.session.evidence.filter(item => item.check === id).at(-1)
      return evidence ? evidence.status : "pending"
    },
    kindLabel(kind) {
      return {automatic_probe: "Checked automatically", assisted_probe: "Your action plus an automatic signal check",
        human_attestation: "Confirmed by you"}[kind]
    },
    statusClass(status) {
      return {pass: "text-bg-success", fail: "text-bg-danger",
        blocked: "text-bg-dark", pending: "text-bg-warning",
        stale: "text-bg-secondary"}[status] || "text-bg-warning"
    },
    statusLabel(status) {
      return {pass: "Verified", fail: "Problem found", blocked: "Could not verify",
        pending: "Not checked", stale: "Expired"}[status] || "Not checked"
    },
    instructionFor(id) {
      return {
        fingerprint: {action: "Touch the fingerprint pad once.", expected: "Biotron changes to the next built-in sound preset once."},
        mute: {action: "Touch the top Mute pad, touch the plant, then touch Mute again.", expected: "The plant produces no notes while muted and produces notes again after unmuting."},
        led: {action: "Touch the plant, then cover and uncover the light sensor.", expected: "The lights on Biotron visibly respond to those actions."},
        audio: {action: "Touch the plant while a synth or music app is listening to Biotron.", expected: "You hear a sound or see incoming notes in the app."}
      }[id] || {action: "Perform the requested action.", expected: "Observe the expected response."}
    },
    buildReport() {
      return createBiotronManualReport({
        session: this.session, firmwareVersion: this.firmwareVersion,
        browserEnvironment: navigator.userAgent
      })
    },
    emitReport() {
      this.$emit("manual_report_changed", this.session ? this.buildReport() : null)
    },
    async downloadReport() {
      if (!this.session) return
      const report = this.buildReport()
      report.report_sha256 = await sha256Hex(JSON.stringify(report), crypto)
      const blob = new Blob([JSON.stringify(report, null, 2)], {type: "application/json"})
      const link = document.createElement("a")
      link.href = URL.createObjectURL(blob)
      link.download = `biotron-manual-diagnostics-${new Date().toISOString().slice(0, 10)}.json`
      link.click()
      URL.revokeObjectURL(link.href)
    }
  }
}
</script>

<style scoped>
.manual-diagnostics { background: #fff; font-size: 1rem; }
.manual-diagnostics button { min-height: 44px; }
.manual-prompt { background: #fff3cd; font-size: 1.05rem; }
.manual-observation { background: #f8f9fa; }
.manual-details summary { cursor: pointer; min-height: 44px; display: flex; align-items: center; }
</style>
